<!--
  @component
  Implement the preview for a List field with subfield(s).
  @see https://decapcms.org/docs/widgets/#List
  @see https://sveltiacms.app/en/docs/fields/list
-->
<script>
  import { VisibilityObserver } from '@sveltia/ui';

  import Subsection from '$lib/components/contents/details/fields/object/subsection.svelte';
  import FieldPreview from '$lib/components/contents/details/preview/field-preview.svelte';
  import { getEntryDraftContext } from '$lib/services/contents/draft/state.svelte';
  import { getFieldValueMapSnapshot } from '$lib/services/contents/draft/value-map.svelte';
  import { getMemoizedSubtree } from '$lib/services/contents/entry/subtree';
  import { getListFieldInfo, getListItemKey } from '$lib/services/contents/fields/list/helpers';

  /**
   * @import { SubtreeMemo } from '$lib/services/contents/entry/subtree';
   * @import { FieldPreviewProps } from '$lib/types/private';
   * @import {
   * ComplexListField,
   * ListFieldWithSubField,
   * ListFieldWithSubFields,
   * ListFieldWithTypes,
   * VariableFieldType,
   * } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {ComplexListField} fieldConfig Field configuration.
   * @property {string[] | undefined} currentValue Field value.
   */

  const entryDraft = getEntryDraftContext();

  /** @type {FieldPreviewProps & Props} */
  let {
    /* eslint-disable prefer-const */
    locale,
    keyPath,
    typedKeyPath,
    fieldConfig,
    /* eslint-enable prefer-const */
  } = $props();

  const { field } = $derived(/** @type {ListFieldWithSubField} */ (fieldConfig));
  const { fields } = $derived(/** @type {ListFieldWithSubFields} */ (fieldConfig));
  const { types, typeKey = 'type' } = $derived(/** @type {ListFieldWithTypes} */ (fieldConfig));
  const { hasSingleSubField, hasVariableTypes } = $derived(getListFieldInfo(fieldConfig));
  /* v8 ignore start -- a list field has either subfields or a single subfield */
  const singleSubFields = $derived(fields ?? (field ? [field] : []));
  /* v8 ignore stop */
  /**
   * What the items were last assembled from, so they’re only assembled again once they’ve changed.
   * A plain object, as it’s only a memo.
   * @type {SubtreeMemo}
   */
  const itemsMemo = {};
  /** @type {Record<string, any>[]} */
  const items = $derived(
    getMemoizedSubtree(
      itemsMemo,
      getFieldValueMapSnapshot(entryDraft.current, locale, keyPath),
      keyPath,
    ) ?? [],
  );
  /**
   * The variable type of each item, or `undefined` for a list without variable types. The `each`
   * block below iterates over these primitives rather than over the items: the items are rebuilt
   * from the snapshot on every update, and a keyed `each` block over them would rewrite the source
   * of every item on every keystroke, which Svelte then walks through every nested preview.
   * @type {(string | undefined)[]}
   */
  const itemTypes = $derived(items.map((item) => (hasVariableTypes ? item[typeKey] : undefined)));
</script>

{#each itemTypes as type, index (getListItemKey(items, index))}
  {@const itemKeyPath = `${keyPath}.${index}`}
  <!-- Mark the item outside its observer, so that focusing a field in it can scroll the preview to
    the item even before it’s rendered -->
  <div role="none" data-lazy-key-path={itemKeyPath}>
    <VisibilityObserver>
      {@const typeConfig = type ? types?.find(({ name }) => name === type) : undefined}
      {#if hasVariableTypes && !typeConfig}
        <!-- Unknown type: a warning is displayed in the editor -->
      {:else}
        {@const label = typeConfig ? typeConfig.label || typeConfig.name : undefined}
        {@const subFields = hasVariableTypes
          ? /** @type {VariableFieldType} */ (typeConfig).fields
          : singleSubFields}
        <Subsection {label}>
          {#each subFields as subField (subField.name)}
            {@const subFieldKeyPath = hasSingleSubField
              ? itemKeyPath
              : `${itemKeyPath}.${subField.name}`}
            <div role="none" data-lazy-key-path={subFieldKeyPath}>
              <VisibilityObserver>
                <FieldPreview
                  keyPath={subFieldKeyPath}
                  typedKeyPath={hasVariableTypes
                    ? `${typedKeyPath}.*<${type}>.${subField.name}`
                    : `${typedKeyPath}.*.${subField.name}`}
                  {locale}
                  fieldConfig={subField}
                />
              </VisibilityObserver>
            </div>
          {/each}
        </Subsection>
      {/if}
    </VisibilityObserver>
  </div>
{/each}
